import { useContext, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import 'app/components/commons/Experience/Availability/utils';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetExperience } from 'app/hooks/data/core/admin/experiences/useExperience';
import { useGetHotel, useHotels } from 'app/hooks/data/hotels/useHotel';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { ExperienceAvailableDayLimit } from 'app/typings/core/admin/experiences/enum/experienceAvailableDayLimit';
import { DEFAULT_EXPERIENCE_AVAILABLE_DAY_LIMIT } from 'app/typings/core/admin/experiences/enum/experienceAvailableDayLimit';
import type { ExperienceResponse } from 'app/typings/core/admin/experiences/experience';
import { ExperienceType } from 'app/typings/experiences';
import { parse } from 'app/utils/dayjs';
import { useSearchParams } from 'app/utils/searchParams';

import { DEFAULT_EXPERIENCE_DATA } from './data';

export type ExperienceInitialFormType = Omit<
  ExperienceResponse,
  'id' | 'createdAt' | 'updatedAt' | 'time'
> & {
  id?: number;
  time?: Date | null;
  experienceType: ExperienceType;
  availableOn?: ExperienceAvailableDayLimit[];
  maxAddonQuantity?: boolean;
  lastMinuteRule?: boolean;
};

export const useExperienceData = () => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const { search } = useLocation();

  const { id: experienceIdParam } = useParams<{ id: string }>();
  const { getSearchParam } = useSearchParams();

  const hotelId = getSearchParam('hotelId');
  const isOriginal = getSearchParam('isOriginal');

  const experienceId = useMemo(
    () => (experienceIdParam ? parseInt(experienceIdParam) : undefined),
    [experienceIdParam]
  );

  const hotelIdFromSearch = useMemo(() => {
    const parsedHotelId = parseInt(
      new URLSearchParams(search).get('hotelId') ?? ''
    );

    return Number.isNaN(parsedHotelId) ? undefined : parsedHotelId;
  }, [search]);

  const isCreatePage = !experienceId;

  const { data: fetchedExperience } = useGetExperience(experienceId);

  const { data: hotel } = useGetHotel(
    fetchedExperience?.hotelId ?? hotelIdFromSearch
  );
  const { data: hotels } = useHotels();
  const { data: experiencesCategories } = useExperiencesCategories();

  const initialValues: ExperienceInitialFormType = useMemo(() => {
    if (isCreatePage) {
      return {
        ...DEFAULT_EXPERIENCE_DATA,
        experienceType:
          hotelId && !isOriginal
            ? ExperienceType.HOTEL
            : ExperienceType.STAYCATION,
        hotelId: hotelId ? parseInt(hotelId) : undefined,
        availableDayLimits: DEFAULT_EXPERIENCE_AVAILABLE_DAY_LIMIT,
        maxAddonQuantity: false,
        lastMinuteRule: false,
        time: undefined,
      };
    }

    return {
      ...DEFAULT_EXPERIENCE_DATA,
      ...fetchedExperience,
      experienceType: !!fetchedExperience?.hotelId
        ? ExperienceType.HOTEL
        : ExperienceType.STAYCATION,
      availableDayLimits:
        fetchedExperience?.availableDayLimits ||
        DEFAULT_EXPERIENCE_AVAILABLE_DAY_LIMIT,
      maxAddonQuantity: !!fetchedExperience?.maxQuantity,
      lastMinuteRule: !!fetchedExperience?.closedDaysBeforeCheckIn,
      time: fetchedExperience?.time
        ? parse(fetchedExperience.time, 'HH:mm', new Date())
        : undefined,
    };
  }, [isCreatePage, fetchedExperience, hotelId, isOriginal]);

  const isDataLoaded = useMemo(() => {
    const baseDataLoaded = !!hotels && !!experiencesCategories;

    if (!baseDataLoaded) {
      return false;
    }

    if (isCreatePage) {
      return !!hotelId || !!isOriginal;
    }

    return !!fetchedExperience;
  }, [
    hotels,
    experiencesCategories,
    isCreatePage,
    hotelId,
    isOriginal,
    fetchedExperience,
  ]);

  const title = isCreatePage
    ? 'Create new activity'
    : experiencesCategories?.find(
        (cat) => cat.id === fetchedExperience?.categoryId
      )?.name || 'Experience Details';

  const buttonSaveText = isCreatePage
    ? 'Create new activity'
    : isEditMode
    ? 'Save'
    : 'Edit';

  return {
    experienceId,
    isCreatePage,
    hotel,
    experiencesCategories,
    initialValues,
    isDataLoaded,
    buttonSaveText,
    title,
    fetchedExperience,
  };
};
